import { datadogRum } from '@datadog/browser-rum';

let isInitialized = false;

export default function configureDatadog() {
  let environment = null;
  const environments = {
    qa1: 'getstarted-qa.tstdev.io',
    qa2: 'getstarted-internal.tstdev.io',
    staging1: 'getstarted-staging.tradestation.io',
    staging2: 'getstarted-staging-2.tradestation.io',
    production: 'getstarted2.tradestation.com',
    production2: 'getstarted2-prod-2.aop-prod-usva.nite.tradestation.com',
  };
  const hostname = window.location.hostname;

  if (hostname === environments.qa1) {
    environment = 'qa:1';
  } else if (hostname === environments.qa2) {
    environment = 'qa:2';
  } else if (hostname === environments.staging1) {
    environment = 'staging:1';
  } else if (hostname === environments.staging2) {
    environment = 'staging:2';
  } else if (hostname === environments.production) {
    environment = 'production';
  } else if (hostname === environments.production2) {
    environment = 'production:2';
  }

  if (!environment) return null;

  isInitialized = true;

  return datadogRum.init({
    applicationId: 'de745f96-c925-4a23-83cd-89c7b4785ea3',
    clientToken: 'pub2bf36a7bbbcf2bd9123fba1652e09d07',
    site: 'datadoghq.com',
    service: 'ellis-app',
    env: environment,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

// Populates the "User" section of the RUM Attributes tab. Safe to call repeatedly:
// each call replaces the user object, so it is re-sent as fields become known.
export function setDatadogUser({
  applicationId, email, name, fdcnId,
}) {
  if (!isInitialized) return;

  // Only send keys we actually have. The SDK coerces id/name/email with String(),
  // so passing a not-yet-known field would record the literal "null"/"undefined".
  const user = {};
  if (fdcnId) user.id = fdcnId;
  if (name) user.name = name;
  if (applicationId) user.applicationId = applicationId;
  if (email) user.email = email;

  if (Object.keys(user).length === 0) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser(user);
}

// FR-012's p95 budget. Exceeding it is a signal in its own right ("policy"), separate from the
// call failing, so a hand-off that succeeds too slowly is still visible in RUM.
export const FORMSTACK_HANDOFF_BUDGET_MS = 2000;

// Latency is recorded under a different action name per outcome so success and failure are not
// averaged into one distribution. Structured key-value only, and identifiers not identities
// (Principle V, VI): the payload carries application_id, never applicant details.
export function trackFormstackHandoff({
  outcome, durationMs, errorKind, applicationId,
}) {
  const overBudget = durationMs > FORMSTACK_HANDOFF_BUDGET_MS;

  // Best-effort by contract. Callers invoke this from inside the hand-off promise chain — on the
  // success path before dispatching success — so a throw here would convert a completed PUT into
  // a rendered failure, and then throw a second, unhandled time from the caller's .catch.
  // datadogRum is routinely blocked by ad-blockers and is absent before configureDatadog runs.
  try {
    datadogRum.addAction(`formstack_handoff_${outcome}`, {
      application_id: applicationId,
      formstack_handoff_duration_ms: durationMs,
      formstack_handoff_outcome: outcome,
      formstack_handoff_over_budget: overBudget,
    });

    const kind = errorKind || (overBudget ? 'policy' : null);
    if (!kind) return;

    datadogRum.addError(new Error(`formstack_handoff_${kind}_error`), {
      application_id: applicationId,
      formstack_handoff_duration_ms: durationMs,
      formstack_handoff_error_kind: kind,
    });
  } catch (e) {
    // Observability must never change the applicant's outcome.
  }
}

// FR-015 / SC-003. The budget is on time-to-*interactive* — route entry to a painted form — not
// on either leg individually: a 2.5s prefill followed by a 2.5s embed load is a five-second wait
// for the applicant, yet both legs would report "under budget" if the threshold were applied per
// phase. Only the `interactive` phase is measured against it; `prefill` and `load` are recorded
// for attribution, so a breach can be traced to the leg that caused it.
export const FORMSTACK_EMBED_BUDGET_MS = 3000;

const INTERACTIVE_PHASE = 'interactive';

// Mirrors trackFormstackHandoff: one action per phase and outcome so the distributions are not
// averaged together, identifiers not identities (Principle V, VI), and best-effort by contract —
// the callers run inside promise chains, where a throw would turn a loaded form into a rendered
// failure. Extra caller arguments (prefillId, formUrl) are deliberately not destructured: the
// resolved URL and the prefill id are bearer-style and must never reach a third party.
export function trackFormstackEmbed({
  phase, outcome, durationMs, errorKind, applicationId,
}) {
  const overBudget = phase === INTERACTIVE_PHASE && durationMs > FORMSTACK_EMBED_BUDGET_MS;

  try {
    datadogRum.addAction(`formstack_embed_${phase}_${outcome}`, {
      application_id: applicationId,
      formstack_embed_duration_ms: durationMs,
      formstack_embed_outcome: outcome,
      formstack_embed_over_budget: overBudget,
      formstack_embed_phase: phase,
    });

    const kind = errorKind || (overBudget ? 'policy' : null);
    if (!kind) return;

    datadogRum.addError(new Error(`formstack_embed_${kind}_error`), {
      application_id: applicationId,
      formstack_embed_duration_ms: durationMs,
      formstack_embed_error_kind: kind,
      formstack_embed_phase: phase,
    });
  } catch (e) {
    // Observability must never change the applicant's outcome.
  }
}
